import React from 'react';
import { PropTypes } from 'prop-types';
import { map, isEmpty, uniqueId } from 'lodash';

import { FormattedMessage } from 'react-intl';
import { format } from '@origo_vl_banking/utils';

import {
  DetailsContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  ActionContainer,
  DataColumn,
  DataRow,
  IE11Wrapper,
} from './styled-components';

const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
const getTable = (data, category, name) => {
  const tableArray = [];

  // Header
  if (category) {
    tableArray.push(
      <TableRow key={`row.${category}`} heading>
        <TableCell first left heading>
          <FormattedMessage id={`${name}.${category}`} />
        </TableCell>
      </TableRow>,
    );
  }

  const dataArray = Object.keys(data);

  map(data, (cellInfo, cellName) => {
    const isValidWithKey = cellInfo && Object.keys(cellInfo).length > 1 && cellInfo.isKey === true;
    const RowWrapper = isIE11 ? IE11Wrapper : React.Fragment;
    const lastRow = dataArray.indexOf(cellName) + 1 === dataArray.length;

    if (isValidWithKey) {
      tableArray.push(
        <RowWrapper key={uniqueId()}>
          <TableCell left>
            <FormattedMessage id={`${name}.${cellName}`} />
          </TableCell>
          <TableCell last={lastRow}>
            <FormattedMessage id={cellInfo.value} />
          </TableCell>
        </RowWrapper>,
      );
    } else if (cellInfo !== null && typeof cellInfo !== 'boolean') {
      tableArray.push(
        <RowWrapper key={uniqueId()}>
          <TableCell left>
            <FormattedMessage id={`${name}.${cellName}`} />
          </TableCell>
          <TableCell last={lastRow}>{format.value(cellInfo)}</TableCell>
        </RowWrapper>,
      );
    }
  });

  let shouldRender = true;

  if (category && tableArray.length === 1) {
    shouldRender = false;
  }

  return shouldRender && tableArray;
};

const Details = ({
  action,
  actionContainerPadding,
  bottomLeft,
  bottomMiddle,
  bottomRight,
  card,
  containerStyle,
  hideLeftMargin,
  name,
  noAlign,
  topLeft,
  topMiddle,
  topRight,
  transitionDisabled,
  leftProps,
  middleProps,
  rightProps,
  topLeftProps,
  topMiddleProps,
  topRightProps,
  bottomLeftProps,
  bottomMiddleProps,
  bottomRightProps,
}) => (
  <DetailsContainer style={containerStyle} transitionDisabled={transitionDisabled}>
    <DataRow
      hideLeftMargin={hideLeftMargin}
      bottomContainer={bottomLeft}
      card={card}
      left
      noAlign={noAlign}
      {...leftProps}
    >
      {!React.isValidElement(topLeft) && typeof topLeft !== 'function'
        ? topLeft && (
        <DataColumn {...topLeftProps}>
          {map(topLeft, (element, heading) => (
            <Table key={`left.${heading}`}>
              <TableBody bottomContainer={bottomLeft}>
                {getTable(element, heading, name)}
              </TableBody>
            </Table>
              ))}
        </DataColumn>
          )
        : topLeft}
      {!React.isValidElement(bottomLeft) && typeof bottomLeft !== 'function' ? (
        <DataColumn {...bottomLeftProps}>
          {map(bottomLeft, (element, heading) => (
            <Table key={`left.${heading}`}>
              <TableBody bottomContainer={bottomLeft}>{getTable(element, heading, name)}</TableBody>
            </Table>
          ))}
        </DataColumn>
      ) : (
        bottomLeft
      )}
    </DataRow>
    <DataRow
      hideLeftMargin={hideLeftMargin}
      bottomContainer={bottomMiddle}
      card={card}
      noAlign={noAlign}
      right={isEmpty(topRight) && isEmpty(bottomRight)}
      {...middleProps}
    >
      {topMiddle !== {} && !React.isValidElement(topMiddle) && typeof topMiddle !== 'function' ? (
        <DataColumn {...topMiddleProps}>
          {map(topMiddle, (element, heading) => (
            <Table key={`right.${heading}`}>
              <TableBody bottomContainer={bottomMiddle}>
                {getTable(element, heading, name)}
              </TableBody>
            </Table>
          ))}
        </DataColumn>
      ) : (
        topMiddle
      )}
      {bottomMiddle !== {} &&
      !React.isValidElement(bottomMiddle) &&
      typeof bottomMiddle !== 'function' ? (
        <DataColumn {...bottomMiddleProps}>
          {map(bottomMiddle, (element, heading) => (
            <Table key={`right.${heading}`}>
              <TableBody bottomContainer={bottomMiddle}>
                {getTable(element, heading, name)}
              </TableBody>
            </Table>
          ))}
        </DataColumn>
      ) : (
        bottomMiddle
      )}
    </DataRow>
    {(!isEmpty(topRight) || !isEmpty(bottomRight)) && (
      <DataRow
        hideLeftMargin={hideLeftMargin}
        right
        bottomContainer={bottomRight}
        card={card}
        noAlign={noAlign}
        {...rightProps}
      >
        {topRight !== {} && !React.isValidElement(topRight) && typeof topRight !== 'function' ? (
          <DataColumn {...topRightProps}>
            {map(topRight, (element, heading) => (
              <Table key={`right.${heading}`}>
                <TableBody bottomContainer={bottomRight}>
                  {getTable(element, heading, name)}
                </TableBody>
              </Table>
            ))}
          </DataColumn>
        ) : (
          topRight
        )}
        {bottomRight !== {} &&
        !React.isValidElement(bottomRight) &&
        typeof bottomRight !== 'function' ? (
          <DataColumn {...bottomRightProps}>
            {map(bottomRight, (element, heading) => (
              <Table key={`right.${heading}`}>
                <TableBody bottomContainer={bottomRight}>
                  {getTable(element, heading, name)}
                </TableBody>
              </Table>
            ))}
          </DataColumn>
        ) : (
          bottomRight
        )}
      </DataRow>
    )}
    {!isEmpty(action) && (
      <ActionContainer actionContainerPadding={actionContainerPadding}>{action}</ActionContainer>
    )}
  </DetailsContainer>
);

Details.propTypes = {
  action: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  actionContainerPadding: PropTypes.string,
  bottomLeft: PropTypes.object,
  bottomMiddle: PropTypes.object,
  bottomRight: PropTypes.object,
  card: PropTypes.bool,
  containerStyle: PropTypes.object,
  hideLeftMargin: PropTypes.bool,
  name: PropTypes.string.isRequired,
  noAlign: PropTypes.bool,
  topLeft: PropTypes.object,
  topMiddle: PropTypes.object,
  topRight: PropTypes.object,
  transitionDisabled: PropTypes.bool,
  leftProps: PropTypes.object,
  middleProps: PropTypes.object,
  rightProps: PropTypes.object,
  topLeftProps: PropTypes.object,
  topMiddleProps: PropTypes.object,
  topRightProps: PropTypes.object,
  bottomLeftProps: PropTypes.object,
  bottomMiddleProps: PropTypes.object,
  bottomRightProps: PropTypes.object,
};

Details.defaultProps = {
  action: [],
  actionContainerPadding: null,
  bottomLeft: {},
  bottomMiddle: {},
  bottomRight: {},
  card: false,
  containerStyle: null,
  noAlign: false,
  topLeft: {},
  topMiddle: {},
  topRight: {},
  transitionDisabled: true,
  leftProps: {},
  middleProps: {},
  rightProps: {},
  topLeftProps: {},
  topMiddleProps: {},
  topRightProps: {},
  bottomLeftProps: {},
  bottomMiddleProps: {},
  bottomRightProps: {},
  hideLeftMargin: false,
};

export default Details;
